/**
 * `ColunaDoRelatorio` (o que o servidor manda) -> `ColumnOption` (o que o
 * `DynamicTable` entende), com FORMATAÇÃO EXPLÍCITA por coluna.
 *
 * Por que explícita, e não a do componente: o `DynamicTable` formata a célula
 * adivinhando pelo NOME da coluna — `includes("Price")` vira moeda,
 * `includes("Stock")` vira número pt-BR, `includes("UpdatedBy")` vira nome de
 * usuário. Isso funciona nas 7 tabelas de cadastro, que nomeiam tudo em inglês.
 * Em relatório quebra dos dois lados: `taxaEntrega` é dinheiro e não formataria,
 * e uma coluna `precoMedio` formataria sozinha, sem ninguém pedir. Aqui cada
 * coluna diz o que é, e o `format` do `ColumnOption` roda ANTES da cascata.
 *
 * A REGRA QUE MANDA NESTE ARQUIVO: **vazio com explicação, nunca zero.**
 * `null` vindo do servidor é ausência de dado. Nunca vira "R$ 0,00" — vira um
 * traço com o motivo no `title`, e é isso que separa "não tenho como calcular"
 * de "calculei e deu zero".
 */
import React from "react";
import type { ColumnOption } from "../../components/DynamicTable";
import type { ColunaDoRelatorio } from "./tipos";

const MOEDA = new Intl.NumberFormat("pt-BR", {
  style: "currency",
  currency: "BRL",
});

const NUMERO = new Intl.NumberFormat("pt-BR");

/** O que a célula mostra quando o valor não existe. Nunca um zero. */
const VAZIO = "—";

export function formatarDinheiro(valor: unknown): string {
  if (valor === null || valor === undefined || valor === "") return VAZIO;
  const numero = typeof valor === "number" ? valor : parseFloat(String(valor));
  if (!Number.isFinite(numero)) return VAZIO;
  return MOEDA.format(numero);
}

export function formatarNumero(valor: unknown): string {
  if (valor === null || valor === undefined || valor === "") return VAZIO;
  const numero = typeof valor === "number" ? valor : parseFloat(String(valor));
  if (!Number.isFinite(numero)) return VAZIO;
  return NUMERO.format(numero);
}

export function formatarPercentual(valor: unknown): string {
  if (valor === null || valor === undefined || valor === "") return VAZIO;
  const numero = typeof valor === "number" ? valor : parseFloat(String(valor));
  if (!Number.isFinite(numero)) return VAZIO;
  return `${NUMERO.format(numero)}%`;
}

/**
 * Data e hora do legado, SEM converter fuso — e o `timeZone: "UTC"` aqui é o
 * que impede a conversão, não o que a faz.
 *
 * O motivo: `createdAt` no OPS é `timestamp WITHOUT time zone` e guarda hora de
 * PAREDE (o relógio do PDV da Lucco). O driver do Postgres monta um `Date`
 * tratando esse valor como se fosse UTC. Se a tela então formatasse no fuso do
 * navegador, uma venda das 18:42 apareceria como 15:42 — três horas para trás,
 * e o dia da virada mudaria junto.
 *
 * Fixando `timeZone: "UTC"` a hora sai exatamente como está gravada. O rodapé
 * do carimbo diz em que fuso ela deve ser lida, e o aviso FUSO_NAO_COMPROVADO
 * diz que essa premissa não é demonstrável com o dado que existe.
 */
export function formatarDataHora(valor: unknown): string {
  if (!valor) return VAZIO;
  const data = new Date(String(valor));
  if (Number.isNaN(data.getTime())) return VAZIO;
  return data.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "UTC",
  });
}

/** O tipo do `ColumnOption` é o do filtro do DynamicTable, não o do valor. */
function tipoDoFiltro(
  tipo: ColunaDoRelatorio["tipo"]
): ColumnOption["type"] {
  return tipo === "data" ? "date" : tipo === "lista" ? "list" : "text";
}

/**
 * Converte a lista do servidor. A ordem é a que o servidor mandou (`ordem`) —
 * o front não reordena por conta própria.
 */
export function paraColunasDaTabela(
  colunas: ColunaDoRelatorio[]
): ColumnOption[] {
  return [...colunas]
    .sort((a, b) => (a.ordem ?? 999) - (b.ordem ?? 999))
    .map((coluna) => ({
      label: coluna.label,
      value: coluna.value,
      type: tipoDoFiltro(coluna.tipo),
      format: (valor: any) => {
        // Coluna sem insumo: a célula fica vazia E EXPLICADA. O motivo vem do
        // servidor, que é quem sabe por que não tem dado.
        if (coluna.motivoDoVazio && (valor === null || valor === undefined)) {
          return React.createElement(
            "span",
            {
              title: coluna.motivoDoVazio,
              style: { color: "#8f5e07", cursor: "help" },
            },
            VAZIO
          );
        }

        switch (coluna.tipo) {
          case "dinheiro":
            return formatarDinheiro(valor);
          case "numero":
            return formatarNumero(valor);
          case "percentual":
            return formatarPercentual(valor);
          case "data":
            return formatarDataHora(valor);
          default:
            return valor === null || valor === undefined || valor === ""
              ? VAZIO
              : String(valor);
        }
      },
    }));
}

/**
 * Põe na frente as colunas que a VISÃO escolhida destaca.
 *
 * Por que isso não é cosmético: a ordem que o servidor manda é a ordem
 * canônica do relatório (Loja, Turno, Dia, Hora, …, e só lá pelo meio
 * Categoria e Produto). Numa visão de MIX, quem abre quer ver produto,
 * quantidade e receita — e esses ficavam fora da área visível, atrás de uma
 * rolagem horizontal. A visão perdia a graça exatamente por não mostrar o que
 * ela existe para mostrar.
 *
 * As demais colunas continuam TODAS presentes, logo atrás: quem quiser
 * comparar com uma dimensão fora da visão só rola ou usa o seletor de colunas.
 * Nada é removido.
 */
export function priorizarColunas(
  colunas: ColumnOption[],
  prioridade: string[]
): ColumnOption[] {
  if (prioridade.length === 0) return colunas;
  const posicao = new Map(prioridade.map((valor, indice) => [valor, indice]));
  const destacadas = colunas
    .filter((coluna) => posicao.has(coluna.value))
    .sort((a, b) => posicao.get(a.value)! - posicao.get(b.value)!);
  const resto = colunas.filter((coluna) => !posicao.has(coluna.value));
  return [...destacadas, ...resto];
}
